import React from "react";
import { Link } from "react-router-dom";
import {
  History,
  Scale,
  Repeat,
  TrendingUp,
  Upload,
  Target,
  PiggyBank,
  Wallet,
  Sparkles,
  HelpCircle,
  UserCog,
  BookOpen,
  Copy,
  Link2,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import HelpButton from "../components/HelpButton";

// App-drawer style tile. Full-screen grid so teens can scan visually.
function Tile({ to, icon: Icon, label, sub, tone, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="nb-card-lg p-4 flex flex-col items-start gap-2 hover:translate-y-[-2px] active:translate-x-0.5 active:translate-y-0.5 transition"
      style={{ background: tone || "#fff", boxShadow: "4px 4px 0 0 #1a1a1a" }}
    >
      <div className="w-10 h-10 rounded-lg border-2 border-[#1a1a1a] bg-white flex items-center justify-center">
        <Icon size={22} strokeWidth={2.75} />
      </div>
      <div className="min-w-0">
        <div className="font-display font-black text-base leading-tight">{label}</div>
        {sub && <div className="text-[11px] font-bold text-[#4a4a4a] leading-snug mt-0.5">{sub}</div>}
      </div>
    </Link>
  );
}

export default function More() {
  const { user } = useAuth();

  return (
    <div className="space-y-5" data-testid="more-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label">Everything else</div>
          <h1 className="font-display font-black text-2xl leading-tight">More stuff</h1>
        </div>
        <HelpButton id="more" />
      </div>

      {/* Money moves */}
      <div>
        <div className="label !mb-2">Money moves</div>
        <div className="grid grid-cols-2 gap-3">
          <Tile
            to="/register"
            testid="more-history"
            icon={History}
            label="History"
            sub="Every spend & deposit"
            tone="#fdecc8"
          />
          <Tile
            to="/reconcile"
            testid="more-match"
            icon={Scale}
            label="Match with bank"
            sub="Line up with your statement"
          />
          <Tile
            to="/recurring"
            testid="more-repeats"
            icon={Repeat}
            label="Repeats"
            sub="Bills & paychecks"
          />
          <Tile
            to="/forecast"
            testid="more-forecast"
            icon={TrendingUp}
            label="Forecast"
            sub="Next 2 weeks"
            tone="#d9d4f1"
          />
        </div>
      </div>

      {/* Plan & save */}
      <div>
        <div className="label !mb-2">Plan & save</div>
        <div className="grid grid-cols-2 gap-3">
          <Tile
            to="/budgets"
            testid="more-budgets"
            icon={Target}
            label="Budgets"
            sub="Cap categories"
          />
          <Tile
            to="/goals"
            testid="more-goals"
            icon={PiggyBank}
            label="Savings goals"
            sub="Stash cash for a thing"
            tone="#b5e4ca"
          />
        </div>
      </div>

      {/* Data */}
      <div>
        <div className="label !mb-2">Data</div>
        <div className="grid grid-cols-2 gap-3">
          <Tile
            to="/import"
            testid="more-import"
            icon={Upload}
            label="Import CSV"
            sub="Drop a bank statement"
          />
          <Tile
            to="/bank-sync"
            testid="more-bank-sync"
            icon={Link2}
            label="Connect bank"
            sub="Auto-import every 6h"
            tone="#c8e6c9"
          />
          <Tile
            to="/duplicates"
            testid="more-duplicates"
            icon={Copy}
            label="Duplicates"
            sub="Clean-up assistant"
          />
          <Tile
            to="/accounts"
            testid="more-accounts"
            icon={Wallet}
            label="Accounts"
            sub="Add or rename banks"
          />
        </div>
      </div>

      {/* You */}
      <div>
        <div className="label !mb-2">You</div>
        <div className="grid grid-cols-2 gap-3">
          <Tile
            to="/profile"
            testid="more-profile"
            icon={UserCog}
            label="Profile"
            sub="SMS, plan, dark mode"
          />
          <Tile
            to="/help"
            testid="more-help"
            icon={HelpCircle}
            label="Help"
            sub="How stuff works"
          />
          {user?.is_admin && (
            <Tile
              to="/admin"
              testid="more-admin"
              icon={Sparkles}
              label="Admin"
              sub="Users, videos, rules"
              tone="#f4c2c2"
            />
          )}
          {user?.is_admin && (
            <Tile
              to="/tour-videos"
              testid="more-tour-videos"
              icon={BookOpen}
              label="Tour videos"
              sub="Attach demos to steps"
            />
          )}
        </div>
      </div>
    </div>
  );
}
